import Box from "@mui/material/Box";
import { GridRowModes, DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { Delete, Get, Post, Put } from "../../Common/WebRequest";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { slice, getStorageItem } from "../../Common/Utility";
import columnBuilder from "./columnBuilder";
import EditToolbar from "./EditToolbar";

export default function BranchesGrid() {
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [branchesData, setBranchesData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [reload, setReload] = useState(false);
  const [regionData, setRegionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      const admin = await getStorageItem("login", "isAdmin");
      setIsAdmin(!!admin);
    })();
  }, []);

  // Delete modal state
  const [type, setType] = useState(null);
  const [id, setId] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);

  const clearMessages = () => {
    setError("");
    setSuccessMsg("");
  };

  const handleError = (error, operation) => {
    console.error(`Error during ${operation}:`, error);
    const errorMessage = error.Status
      ? `${error.Status}/${error.Error}/${error.ErrorDetail}`
      : error.message || "Ein unerwarteter Fehler ist aufgetreten";
    setError(errorMessage);
    setSuccessMsg("");
  };

  const loadData = async () => {
    setIsLoading(true);
    clearMessages();

    try {
      const [branchRes, regionsRes] = await Promise.all([
        Get("branch"),
        Get("region"),
      ]);

      if (!branchRes.isSuccess) throw branchRes;
      if (!regionsRes.isSuccess) throw regionsRes;

      setBranchesData(branchRes.result);
      setRegionData(regionsRes.result);
    } catch (err) {
      handleError(err, "data loading");
      setBranchesData([]); // Reset to empty on error
      setRegionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [reload]);

  const showDeleteModal = (type, id) => {
    try {
      const branchToDelete = branchesData.find((x) => x.id === id);
      if (!branchToDelete) throw new Error("Abrechnungstelle nicht gefunden");

      setType(type);
      setId(id);
      setDeleteMessage(
        `Möchten Sie „${branchToDelete.branchName}" wirklich löschen?`
      );
      setDisplayConfirmationModal(true);
    } catch (err) {
      handleError(err, "delete modal");
    }
  };

  const executeDelete = async () => {
    clearMessages();

    try {
      const deleteResponse = await Delete("branch", id);
      if (!deleteResponse.isSuccess) throw deleteResponse;

      setSuccessMsg("Die Abrechnungstelle wurde erfolgreich gelöscht.");
      setReload(!reload);
    } catch (err) {
      handleError(err, "deletion");
      await loadData(); // Reload data to ensure consistency
    }
  };

  const handleDeleteClick = (id) => () => showDeleteModal("", id);

  const handleAfterDeleteClick = () => {
    setDisplayConfirmationModal(false);
    executeDelete();
  };

  async function handleSaveOperation(newRow, updatedRow) {
    clearMessages();
    const originalData = [...branchesData];

    try {
      setBranchesData(
        branchesData.map((row) => (row.id === newRow.id ? updatedRow : row))
      );

      const saveResponse =
        updatedRow.id === 0
          ? await Post("branch", { ...updatedRow, isNew: true })
          : await Put("branch", updatedRow.id, newRow);

      if (!saveResponse.isSuccess) {
        throw saveResponse;
      }

      setSuccessMsg(
        updatedRow.id === 0
          ? "Die neue Abrechnungstelle wurde hinzugefügt."
          : "Die Abrechnungstelle wurde aktualisiert."
      );

      // Add delay before reload to allow message to be shown
      setTimeout(() => {
        setReload(!reload);
      }, 1000);
    } catch (err) {
      handleError(err, "saving");
      setBranchesData(originalData); // Restore original data
      loadData(); // Ensure data consistency
      throw err; // Re-throw to trigger grid error handling
    }
  }

  const handleEditClick = (id) => () => {
    clearMessages();
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "branchName" },
    });
  };

  const handleCancelClick = (id) => () => {
    try {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });

      const editedRow = branchesData.find((row) => row.id === id);
      if (editedRow?.isNew) {
        setBranchesData(branchesData.filter((row) => row.id !== id));
      }
      clearMessages();
    } catch (err) {
      handleError(err, "canceling edit");
      loadData();
    }
  };

  const handleSaveClick = (id) => async (event) => {
    event.stopPropagation();
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  async function processRowUpdate(newRow) {
    try {
      const updatedRow = { ...newRow, isNew: false };
      await handleSaveOperation(newRow, updatedRow);
      return updatedRow;
    } catch (err) {
      handleError(err, "row update");
      throw err; // This ensures the grid knows the update failed
    }
  }

  const columns = columnBuilder({
    regionData,
    rowModesModel,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
    isAdmin,
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box
      sx={{
        height: 800,
        width: "95%",
        "& .actions": { color: "text.secondary" },
        "& .textPrimary": { color: "text.primary" },
      }}
    >
      <br />
      <h2>Abrechnungstelle</h2>
      <br />
      {error && (
        <div className="alert alert-danger" role="alert">
          {slice(error, 200, "...")}
        </div>
      )}

      {!error && successMsg && (
        <div className="alert alert-success" role="alert">
          {slice(successMsg, 100, "...")}
        </div>
      )}
      <br />
      <DataGrid
        rowSelection={false}
        density="compact"
        hideFooterPagination
        rows={branchesData}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStart={(params, event) => {
          if (!isAdmin) event.defaultMuiPrevented = true;
        }}
        onRowEditStop={(params, event) => (event.defaultMuiPrevented = true)}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(err) => handleError(err, "grid update")}
        slots={{
          toolbar: EditToolbar,
        }}
        slotProps={{
          toolbar: { setBranchesData, setRowModesModel, isAdmin },
        }}
      />
      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={handleAfterDeleteClick}
        hideModal={() => setDisplayConfirmationModal(false)}
        type={type}
        id={id}
        message={deleteMessage}
      />
    </Box>
  );
}
